import React, {
  forwardRef,
  ComponentPropsWithoutRef,
  ComponentPropsWithRef,
} from "react";
import PropTypes from "prop-types";

import NavigationInternalLinkDefault from "@/icons/Navigation/InternalLink/Default";

import type {
  ReactDefaultPropsWithChildren,
  ReactComponentStaticProps,
} from "../../types/react";

import { Icon, IconType, IconProps } from "../Icon";
import { EqualHeightElement } from "../EqualHeight/EqualHeightElement";
import { CopyText, CopyTextProps, CopyTextDefaultTagName } from "../CopyText";

import styles from "./ContentTileNavigation.scss";

export const NavTileHeadingDefaultTagName = CopyTextDefaultTagName;
export type NavTileHeadingDefaultTag = typeof NavTileHeadingDefaultTagName;

type NavTileHeadingBaseProps<
  Tag extends React.ElementType = NavTileHeadingDefaultTag
> = {
  tag?: Tag;
  variants?: CopyTextProps["variants"];
  icon?: IconType;
  iconWrapperProps?: IconProps["wrapperProps"];
};

export type NavTileHeadingProps<
  Tag extends React.ElementType = NavTileHeadingDefaultTag
> = NavTileHeadingBaseProps<Tag> &
  Omit<ComponentPropsWithoutRef<"div">, keyof NavTileHeadingBaseProps<Tag>>;

const defaultVariants = [CopyText.Variant.highlight];

const defaultProps = {
  children: "",
  icon: NavigationInternalLinkDefault,
  iconWrapperProps: undefined,
  variants: defaultVariants,
  tag: NavTileHeadingDefaultTagName,
} as const;

const validatedDefaultProps: ReactDefaultPropsWithChildren<
  NavTileHeadingBaseProps,
  typeof defaultProps
> = defaultProps;

const NavTileHeadingWithRef = <
  Tag extends React.ElementType = NavTileHeadingDefaultTag
>(
  props: NavTileHeadingProps<Tag>,
  ref: ComponentPropsWithRef<"div">["ref"]
) => {
  const {
    children,
    icon = defaultProps.icon,
    iconWrapperProps,
    tag,
    variants,
    ...others
  } = props;
  return (
    <EqualHeightElement name="navTileHeading">
      <div className={styles.headingWrapper} ref={ref} {...others}>
        <span className={styles.headingIcon}>
          <Icon
            icon={icon}
            size={Icon.Size.parent}
            wrapperProps={iconWrapperProps}
          />
        </span>
        <CopyText
          tag={tag as CopyTextProps["tag"]}
          variants={variants}
          customClass={styles.headingText}
        >
          {children}
        </CopyText>
      </div>
    </EqualHeightElement>
  );
};

export const NavTileHeading = (forwardRef(
  NavTileHeadingWithRef
) as unknown) as (<Tag extends React.ElementType = NavTileHeadingDefaultTag>(
  props: NavTileHeadingProps<Tag>
) => ReturnType<typeof NavTileHeadingWithRef>) &
  ReactComponentStaticProps<NavTileHeadingProps, "tag">;

NavTileHeading.propTypes = {
  icon: PropTypes.elementType as PropTypes.Requireable<IconType>,
  iconWrapperProps: PropTypes.shape({}),
  tag: PropTypes.string,
  variants: CopyText.propTypes.variants,
  children: PropTypes.oneOfType([
    PropTypes.arrayOf(PropTypes.node),
    PropTypes.node,
  ]).isRequired,
} as const;

NavTileHeading.defaultProps = validatedDefaultProps;

NavTileHeading.displayName = "ContentTileNavigation.Item.Heading";

export default NavTileHeading;
